import React, { useEffect, useState } from 'react';
import { Col, Row, Table, Typography, Divider, Button } from 'antd';
import { useTranslation } from 'react-i18next';

import useTable from './useTable';
import ErrorMessage from '../ErrorMessage';

type Props = {
  estimatePriceData: Response.EstimatePrice['item'];
  queryTaxes?: Response.TaxesResponse['items'];
  enableBackOrder: boolean;
  setEnableBackOrder: (val: boolean) => void;
  onHandleCalculate: (formValue: any, type: any) => void;
  formValue: any;
  isProductFinished: boolean;
};

const CalculateResultTable: React.FC<Props> = ({
  estimatePriceData,
  queryTaxes,
  enableBackOrder,
  setEnableBackOrder,
  onHandleCalculate,
  formValue,
  isProductFinished,
}) => {
  const { t } = useTranslation();
  const [errorLists, setErrorLists] = useState<string[]>([]);
  const { tableProps } = useTable(
    estimatePriceData,
    queryTaxes,
    isProductFinished,
  );

  useEffect(() => {
    const errors: string[] = [];
    if (estimatePriceData?.totalMoneyExceedAllowed) {
      errors.push(t('message.E000063'));
    }
    setErrorLists(errors);
  }, [estimatePriceData, t]);

  return (
    <>
      <div className="content_pricing_table_title custom_content_pricing_table_title">
        {estimatePriceData?.hasUpdateFinishingSize && (
          <Typography.Title level={5} className="note">
            {t('pricing_page.notes.text4')}
          </Typography.Title>
        )}
        <Typography.Title
          level={1}
          className="mt-20 card_title card_title-order"
        >
          {t('pricing_page.estimate_results.title')}
        </Typography.Title>
        <Divider className="mt-5 mb-0 card_divider" />
      </div>
      <div className="custom_content_pricing_table">
        <Row>
          <Col span={24}>
            <Table {...tableProps} className="content_pricing_table" />
          </Col>
        </Row>
        {errorLists?.map((error, index) => (
          <ErrorMessage key={index} content={error} />
        ))}

        {estimatePriceData && (
          <>
            {!enableBackOrder ? (
              (estimatePriceData?.hasCheap || estimatePriceData?.hasQuick) && (
                <div className="re_estimate_container">
                  <Typography.Title
                    level={5}
                    className="re_estimate_condition_title title_re_estimate"
                  >
                    {t('pricing_page.re_estimate.note')}
                  </Typography.Title>
                  <Row className="re_estimate custom_re_estimate">
                    {estimatePriceData?.hasCheap && (
                      <Col
                        span={24}
                        className="re_estimate_condition custom_re_estimate_condition "
                      >
                        <Col
                          span={18}
                          lg={14}
                          xl={18}
                          className="container_condition_title"
                        >
                          <Typography.Title
                            level={5}
                            className="re_estimate_condition_title"
                          >
                            {t('pricing_page.re_estimate.cheaper')}
                          </Typography.Title>
                        </Col>
                        <Col span={6} lg={10} xl={6}>
                          <Button
                            type="primary"
                            onClick={() => {
                              setEnableBackOrder(true);
                              onHandleCalculate(formValue, 'PRICE_LOW');
                            }}
                          >
                            {t('pricing_page.re_estimate.button')}
                          </Button>
                        </Col>
                      </Col>
                    )}
                    {estimatePriceData?.hasQuick && (
                      <Col
                        span={24}
                        className="re_estimate_condition"
                        style={{
                          width: '100%',
                        }}
                      >
                        <Col
                          span={18}
                          lg={14}
                          xl={18}
                          className="container_condition_title"
                        >
                          <Typography.Title
                            level={5}
                            className="re_estimate_condition_title"
                          >
                            {t('pricing_page.re_estimate.faster')}
                          </Typography.Title>
                        </Col>
                        <Col span={6} lg={10} xl={6}>
                          <Button
                            type="primary"
                            onClick={() => {
                              setEnableBackOrder(true);
                              onHandleCalculate(formValue, 'FAST_DELIVERY');
                            }}
                          >
                            {t('pricing_page.re_estimate.button')}
                          </Button>
                        </Col>
                      </Col>
                    )}
                  </Row>
                </div>
              )
            ) : (
              <div className="re_estimate_container">
                <Divider className="form_divider" />
                <Row className="initial_estimate mb-10">
                  <Col span={24}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setEnableBackOrder(false);
                        onHandleCalculate(formValue, 'BACK_ORDER');
                      }}
                    >
                      {t('pricing_page.re_estimate.initial_estimate_result')}
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CalculateResultTable;
